import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import DoctrinePillars from "@/components/site/DoctrinePillars";
import Sentinel22 from "@/components/site/Sentinel22";
import WelfareSupremacy from "@/components/site/WelfareSupremacy";
import GuidingPrinciples from "@/components/site/GuidingPrinciples";
import Leadership from "@/components/site/Leadership";
import CTABand from "@/components/site/CTABand";

const TIERS = [
  {
    tier: "Tier I",
    label: "Controlled Environments",
    body:
      "Predictable noise, limited participant density, structured interaction. Classrooms in session, small group programs, planned community gatherings.",
  },
  {
    tier: "Tier II",
    label: "Moderately Stimulating",
    body:
      "Fluctuating participant flow, variable noise. Engagement narrows, recovery windows widen, exposure is carefully governed.",
  },
  {
    tier: "Tier III",
    label: "Elevated Stimulus",
    body:
      "High emotional resonance or media presence. Deployment is brief, highly controlled, and followed by mandatory recovery for our partners.",
  },
];

const STOP_CONDITIONS = [
  "Any sign of canine stress, fatigue, or operational unwillingness.",
  "Environmental conditions exceeding the team's deployment tier.",
  "Handler judgment that the interaction no longer supports stabilization.",
  "Institutional request that conflicts with welfare or doctrine.",
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "Doctrine · MSWDC";
  }, []);

  return (
    <main data-testid="about-page" className="bg-white">
      {/* Header */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-20 md:pb-28">
          <div className="flex items-center gap-3">
            <span className="brand-rule" />
            <span className="overline">The Doctrine, in plain language</span>
          </div>
          <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[1.02] text-slate-900 max-w-5xl">
            A structured, non-clinical framework for stabilizing the rooms that other
            people work in.
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-6 max-w-5xl">
            <p className="text-slate-600 leading-relaxed md:col-span-2">
              MSWDC operates an architecture called <strong className="text-slate-900">Animal-Assisted
              Environmental Stabilization Support</strong>. Trained canine-handler teams provide
              consistent, controlled presence — reducing baseline tension, interrupting
              early escalation signals, and supporting the staff who carry the load.
            </p>
            <p className="text-slate-500 leading-relaxed text-sm">
              This is an operational discipline, not a therapeutic service. Every
              deployment is governed by a codified doctrine, classified environmental
              tiers, and absolute handler halt-authority.
            </p>
          </div>
        </div>
      </section>

      {/* Origin / Why we exist */}
      <section className="bg-[#FAF7EC] border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 md:gap-16">
            <div className="col-span-12 md:col-span-4">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Origin</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-4xl tracking-tight leading-[1.05] text-slate-900">
                Built to endure faithfully.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <blockquote className="border-l-2 border-amber-600 pl-6">
                <p className="font-display text-2xl md:text-3xl leading-snug text-slate-900 max-w-3xl">
                  "The Midnight Sun Working Dog Coalition was not formed to chase
                  urgency. It was formed to embody disciplined presence. It was not
                  built to expand rapidly. It was built to endure faithfully."
                </p>
                <footer className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600">
                  Institutional Bible · Vol. I
                </footer>
              </blockquote>
              <p className="mt-10 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
                Shaped by the vastness and constancy of Alaska, we are an organization
                of restraint before reaction, protection before ambition, and humility
                before authority. We stand watch — quietly, steadily, and deliberately.
              </p>
            </div>
          </div>
        </div>
      </section>

      <DoctrinePillars />

      <Sentinel22 compact />

      {/* Environmental Tier System */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Environmental Tier System</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Every space is assessed. The team adapts — not the other way around.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-md">
                Posture, proximity, and engagement protocol are determined by the
                environment's tier. Welfare and safety always supersede demand.
              </p>
            </div>

            <div className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {TIERS.map((t) => (
                <div
                  key={t.tier}
                  data-testid={`tier-${t.tier.replace(/\s+/g, "-").toLowerCase()}`}
                  className="border border-slate-200 p-6 md:p-8 hover:bg-slate-50 transition-colors"
                >
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                    {t.tier}
                  </div>
                  <h3 className="mt-4 font-display font-bold text-2xl text-slate-900">
                    {t.label}
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <WelfareSupremacy />

      <GuidingPrinciples />

      {/* Stop Conditions */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Stop Conditions</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Non-negotiable thresholds.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-md">
                A reliable system knows precisely when to stop. These are the
                conditions under which a deployment pauses or ends — no override,
                no exception.
              </p>
            </div>

            <ul className="col-span-12 md:col-span-7 space-y-0 border-t border-slate-200">
              {STOP_CONDITIONS.map((s, i) => (
                <li
                  key={i}
                  data-testid={`stop-cond-${i}`}
                  className="flex items-start gap-6 py-6 border-b border-slate-200"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-slate-800 text-base md:text-lg leading-relaxed">{s}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <Leadership />

      {/* Governance closing */}
      <section className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Governance</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Leadership is temporary. Doctrine is permanent.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:pt-10">
              <p className="text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
                Founder-led and board-governed, MSWDC selects stewards by{" "}
                <strong className="text-slate-900">character over charisma</strong>,
                ethical consistency, emotional regulation, and devotion to the doctrine.
                The 50-year question — <em>will this decision still serve the mission in
                half a century?</em> — governs every strategic choice.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                For permissions, licensing, or partnership inquiries, please use the
                contact form or reach our team directly.
              </p>
              <Link
                to="/contact"
                data-testid="about-contact-cta"
                className="mt-8 inline-flex items-center gap-3 bg-slate-900 hover:bg-amber-600 text-white px-6 py-4 font-semibold text-sm transition-colors"
              >
                Contact the Coalition
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
